// Die Rechte je Rolle (E3; Gernots Entscheidung 3, 21.10.2026): Jede Rolle
// sieht ihre Sache, die Leitung alles. Was welche Rolle darf, steht HIER, in
// einer Liste „Recht → Rollen“; der Server prüft sie je Route (Liste „Route →
// Recht“ in server/src/crm/rechte-je-route.ts), die Apps richten danach ihre
// Sicht ein. Warum schon in E3: Rechte nachträglich in jede Route zu tragen
// ist teuer, eine Liste jetzt ist billig (PLAN.md, E3, „Rollen und Rechte“).
//
// Grundregeln:
//  - Verweigern ist der Standard: Was nicht in der Liste steht, darf niemand.
//  - Die Leitung steht in JEDER Liste – sie darf alles (ein Test prüft das).
//  - Ein Recht sagt, WAS jemand tun darf, nicht an WELCHEM Datensatz. Dass der
//    Verkauf nur die eigenen Besichtigungen sieht, regelt der Leser am Server
//    (server/src/crm/waechter.ts, leserFuer). Rechte je Datensatz und mehrere
//    Rollen je Person gibt es in E3 nicht (PLAN.md, E3, „Nicht in E3“).
//  - Die Doku-App (draußen am Handy) ist für den Verkauf; die Leitung darf sie
//    auch nutzen (Manuel verkauft selbst) – DOKU_ROLLEN.
//  - Die Rolle Lager darf seit E3c nichts mehr: Es gibt kein Lager mehr (H26),
//    vergeben lässt sie sich nicht (VERGEBBARE_ROLLEN, vertrag.ts), und
//    Migration 0009 bricht ab, solange jemand sie noch aktiv hat. Der Wert
//    bleibt in ROLLEN – die Datenbank kennt ihn (PLAN.md, E3c, „Rolle Lager“).

import type { Rolle } from "./vertrag.ts";

/** Je Recht die Rollen, die es haben. Neue Rechte kommen hier dazu, nie frei im Code. */
export const RECHTE = {
  /** Die Startseite des CRM mit dem Weg aller Anlagen und den Zahlen (Manuels Übersicht, S2). */
  uebersicht: ["leitung", "buero"],
  /** Die Liste der Besichtigungen – der Verkauf sieht darin nur die eigenen (das regelt der Leser). */
  "aufnahmen-liste": ["leitung", "buero", "verkauf"],
  /** Eine Besichtigung ganz samt Dateien – Montage und Inbetriebnahme für die Unterlage; der Verkauf nur die eigenen. */
  "aufnahme-lesen": ["leitung", "buero", "verkauf", "montage", "inbetriebnahme"],
  /** Freigeben, das Material nach der Freigabe ändern, „gesehen“ des Büros. */
  freigeben: ["leitung", "buero"],
  /** Die Standardliste fürs Material lesen und pflegen. */
  standardliste: ["leitung", "buero"],
  /** Die Unterlage für den Monteur. */
  unterlagen: ["leitung", "buero", "montage", "inbetriebnahme"],
  /** Kunden und Anlagen, die Akte, Kunde/Objekt/Anlage berichtigen. */
  kunden: ["leitung", "buero"],
  /** Fragen lesen, stellen, beantworten, „erledigt“ – nur an dem, was der Leser sehen darf (Grundsatz 9). */
  fragen: ["leitung", "buero", "verkauf", "montage", "inbetriebnahme"],
  /** Leute und Geräte verwalten: einladen, freigeben, sperren. */
  leute: ["leitung", "buero"],
  /** Die Rolle Leitung vergeben oder entziehen, Rollen ändern – und alles, was einen Zugang der Leitung betrifft. */
  "rollen-vergeben": ["leitung"],
  /**
   * Einstellungen der Firma: das Jahresziel für Manuels Zähler (O58); später
   * die Aufbewahrung (O19). Nur die Leitung – das sind Vorgaben der Firma,
   * keine Arbeit des Büros.
   */
  einstellungen: ["leitung"],
  // Seit E3c (der Happy-Energy-Ablauf, PLAN.md „E3c“; Prüfung des Plans, Befund 12):
  /** Die Lieferanten und Partien der Firma lesen und ihre Vorgaben (Mailtexte, Vorlauf) – das Büro wählt daraus bei Bestellung und Partien. */
  partner: ["leitung", "buero"],
  /** Lieferanten und Partien anlegen, ändern und „nicht mehr anbieten“ – Vorgaben der Firma wie die Einstellungen. */
  "partner-pflegen": ["leitung"],
  /** Bestellen beim Lieferanten: je Teil der Lieferant, Wunschliefertermin, Anzahl und Fabrikat der Module, „bestellt“ (S3). */
  bestellen: ["leitung", "buero"],
  /** Die Dachpartie beauftragen und die Bodenpartie einteilen (S4). */
  einsatz: ["leitung", "buero"],
  /**
   * Seit S2: „Zu tun“ – je Anlage die Schritte, die jetzt gehen (an Stelle
   * von „Zu prüfen“). Das Büro und die Leitung; der Verkauf behält „Meine
   * Besichtigungen“ (aufnahmen-liste).
   */
  "zu-tun": ["leitung", "buero"],
  /**
   * Seit S6: „Anlage löschen“ – samt Besichtigungen, Fotos, Memos, Fragen und
   * Bestellungen, mit Kunde und Objekt, wenn nichts anderes daran hängt. Nur
   * die Leitung, wie „rollen-vergeben“ (Prüfung des Plans E3c, Befund 12):
   * Löschen echter Daten ist keine Arbeit des Büros.
   */
  "anlage-loeschen": ["leitung"],
} satisfies Record<string, readonly Rolle[]>;

export type Recht = keyof typeof RECHTE;

/** Dieselbe Liste, nur weiter getypt – damit jede Rolle gefragt werden kann. */
const LISTE: Record<Recht, readonly Rolle[]> = RECHTE;

/** Darf diese Rolle das? Unbekanntes (Rolle oder Recht) heißt immer: nein. */
export function darf(rolle: Rolle, recht: Recht): boolean {
  return Object.hasOwn(LISTE, recht) && LISTE[recht].includes(rolle);
}

/** Wer in die Doku-App hinein darf: der Verkauf und die Leitung. */
export const DOKU_ROLLEN = ["verkauf", "leitung"] as const satisfies readonly Rolle[];

export function darfDoku(rolle: Rolle): boolean {
  return (DOKU_ROLLEN as readonly Rolle[]).includes(rolle);
}
